import CsiFormData from "csi/action/FormData";
import { QRY_LIMIT_PATH } from "csi/constants";
import def from "csi/def";
import { Context } from "csi/def/Context";
import { View } from "csi/def/View";
import { ControllerSaveFunction } from "csi/middleware/common";
import reflection from "csi/reflection";
import { resolveNodes } from "csi/service/nodeService";
import { submitWriter } from "csi/service/writerService";
import WorkingCache from "csi/state/WorkingCache";
import { TableWidget } from "csi/table/Widget";
import { C8Node, DUMMY, ICache, cnl } from "csi/typings";
import { isNullOrUndefined } from "csi/utility/coreUtility";
import { ReactionStore } from "csi/utility/mobxUtility";
import { formatNumberForEditing, getPercentageNumber } from "csi/utility/numberUtility";
import csiView from "csi/view";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { ILocaleProvider } from "designSystem/locale";
import { green, red } from "designSystem/styles/color";
import { ComponentItem } from "designSystem/types/component-item-types";
import { ObservableMap, action, computed, keys, makeObservable, observable, runInAction } from "mobx";
import { createContext, useContext } from "react";
import { roundToNearestEighth } from "../CareAndComposition/BaseCompositionStore";

export const TYPE_SIZE_CHART_ADMIN = "SizeChartAdmin";
export const TYPE_COMPOSITION = "Composition";

/** create composition material key */
export function createCompositionKey(compositionMaterialUrl: string, currentMaterialAttributeId?: string): string {
	const key = currentMaterialAttributeId ? `${compositionMaterialUrl}/${currentMaterialAttributeId}` : compositionMaterialUrl;
	return key;
}

/** create composition type key */
export function createCompositionTypeKey(index: number, typeCnl: string): string {
	return typeCnl + index.toString();
}

/** indicate if material comp or type percentage is valid */
function percentageIsInvalid(reachLimit: boolean, isDirty: boolean, percentage?: number): boolean {
	const percentageIsNullOrUndefined = isNullOrUndefined(percentage);
	return !(
		(!isDirty && percentageIsNullOrUndefined) ||
		(isDirty && !percentageIsNullOrUndefined && percentage !== 0 && reachLimit)
	);
}

export class CompositionType {
	@observable private _isDirty: boolean;
	@observable private _percentage: number;
	@observable private _url: string;
	@observable.ref private _currentValue: ComponentItem;
	private readonly compositionStore: CompositionStore;

	/** indicate if type percentage is valid */
	get compositionTypePercentageIsInvalid(): boolean {
		const { compositionStore, isDirty, percentage } = this;
		return percentageIsInvalid(compositionStore.reachLimit, isDirty, percentage);
	}

	get currentValue(): ComponentItem {
		return this._currentValue;
	}

	/** flag (user has previously made a change to percentage) */
	get isDirty(): boolean {
		return this._isDirty;
	}

	/** get percentage per Composition Type */
	get percentage(): number {
		return this._percentage;
	}

	/** get percentage fixed to the length of the attribute */
	get percentageFixed(): string {
		if (!this._percentage) {
			return "";
		}
		return formatNumberForEditing(this._percentage/100, this.compositionStore.percentageAttribute.getFormat());
	}
	

	/** get url per Composition Type */
	get url(): string {
		return this._url;
	}

	@action setCompositionTypeUrl = (event: React.ChangeEvent<{}>, item: ComponentItem): void => {
		const { id, node } = item;

		this._url = id;
		this._currentValue = {
			id,
			node,
			res: node ? undefined : "CompositionNoSelectedItems",
		};
	};

	setCompositionTypePercentage = (event: React.ChangeEvent<HTMLInputElement>): void => {
		let amount = parseFloat(event.target.value);

		if (isNaN(amount)) {
			amount = null;
		}

		runInAction(() => {
			this._percentage = amount;
			this._isDirty = true;
		});
	};

	/** set percentage */
	@action setPercentage = (percentage: number): void => {
		this._percentage = percentage;
	};

	/** set url */
	@action setUrl = (url: string): void => {
		this._url = url;
	};

	constructor(compositionStore: CompositionStore, url: string, percentage: number, currentCompositionItem: ComponentItem) {
		makeObservable(this);
		this.compositionStore = compositionStore;
		this._isDirty = !isNullOrUndefined(percentage);
		this._url = url;
		this._percentage = percentage;
		this._currentValue = currentCompositionItem;
	}
}

export class CompositionMaterial {
	@observable private _isDirty: boolean;
	@observable private _noSustainPercentage: number = 0;
	@observable private _compositionTypes: CompositionType[];
	@observable private _key: string;
	private readonly compositionStore: CompositionStore;

	/** composition material */
	readonly compositionMaterialUrl: cnl;

	/** working cache */
	readonly cache: ICache;

	/** Returns a boolean indicating whether the add new type button should be disabled or not. */
	get buttonDisabled(): boolean {
		return this.compositionTypes.some((type) => type.url === DUMMY);
	}

	/** unique id */
	get id(): string {
		return this._key;
	}

	/** is dirty flag (user has previously made a change to percentage) */
	get isDirty(): boolean {
		return this._isDirty;
	}

	/** percentage of a composition if the user does not have sustainability */
	get noSustainPercentage(): number {
		return this._noSustainPercentage;
	}

	/** formatted percentage of a composition if the user does not have sustainability */
	get noSustainPercentageFixed(): string {
		if (!this.noSustainPercentage) {
			return "";
		}
		return formatNumberForEditing(this.noSustainPercentage/100, this.compositionStore.percentageAttribute.getFormat());
	}

	/** composition node, which equals composition material  */
	get compositionNode(): C8Node {
		const { cache, compositionMaterialUrl } = this;
		return cache.get(compositionMaterialUrl);
	}

	/** gets all possible Sustainability Composition Types */
	get possibleSustainabilityCompositionTypes(): string[] {
		return [DUMMY, ...this.compositionNode.SustainabilityCompositionTypes];
	}

	get compositionKey(): string {
		return this._key;
	}

	/** gets a list of composition Types on a given composition */
	get compositionTypes(): CompositionType[] {
		return this._compositionTypes;
	}

	get usedCompositionTypesUrls(): string[] {
		const usedCompositionTypesUrls = [];
		this.compositionTypes.forEach(({ url }) => {
			usedCompositionTypesUrls.push(url);
		});
		return usedCompositionTypesUrls;
	}

	/** get the nextCompositionType for adding a new variant */
	get nextCompositionType(): string {
		return this.possibleSustainabilityCompositionTypes.filter((item) => !this.usedCompositionTypesUrls.includes(item))[0];
	}

	@action addCompositionType(url: string, percentage?: number): void {
		let currentComponentItem;
		if (url === DUMMY) {
			const valueName = this.possibleSustainabilityCompositionTypes.length === 1 ? "CompositionNoAssignTypes" : "CompositionTypeNoSelectedItems";
			currentComponentItem = { id: DUMMY, res: valueName };
		} else {
			currentComponentItem = {
				id: url,
				node: this.compositionStore.cache.get(url),
			};
		}
		this._compositionTypes.push(new CompositionType(this.compositionStore, url, percentage, currentComponentItem));
	}

	@action removeCompositionType = (index: number): void => {
		this._compositionTypes.splice(index, 1);
	};

	/** set percentage */
	setCompositionMaterialPercentage = (event: React.ChangeEvent<HTMLInputElement>): void => {
		let amount = parseFloat(event.target.value);

		if (isNaN(amount)) {
			amount = null;
		}

		runInAction(() => {
			this._noSustainPercentage = amount;
			this._isDirty = true;
		});
	};

	setItemsForDropdown = (url: string): ComponentItem[] => {
		let items: ComponentItem[] = [
			{ id: DUMMY, res: "CompositionNoAssignNames" },
		];

		// take the currently used composition types and break them into just urls
		const usedCompositionTypesUrls = this.compositionTypes.map(({ url }) => url);
		// filter used composition types out of possible composition types
		const possibleCompositionTypes = [];
		if (this.possibleSustainabilityCompositionTypes.length > 1) {
			this.possibleSustainabilityCompositionTypes.forEach((compositionTypeCandidateUrl) => {
				if (compositionTypeCandidateUrl === DUMMY) {
					if (url === DUMMY || !this.compositionTypes.some((type) => type.url === DUMMY)) {
						items = [
							{ id: DUMMY, res: "CompositionNoSelectedItems" },
						];
					} else {
						items = [];
					}
				} else if (!usedCompositionTypesUrls.includes(compositionTypeCandidateUrl) || compositionTypeCandidateUrl === url) {
					const compositionTypeCandidateNode = this.compositionStore.cache.get(compositionTypeCandidateUrl);
					if (compositionTypeCandidateNode.Active || compositionTypeCandidateUrl === url) {
						possibleCompositionTypes.push(compositionTypeCandidateNode);
					}
				}
			});
			items = [
				...items,
				...possibleCompositionTypes.map((item) => ({
					id: item.$URL,
					invalid: !item.Active,
					node: this.compositionStore.cache.get(item.$URL),
				})),
			];
		}
		return items;
	};

	/** indicate if percentage is valid or not */
	get compositionMaterialPercentageIsInvalid(): boolean {
		return percentageIsInvalid(this.compositionStore.reachLimit, this.isDirty, this.noSustainPercentage);
	}

	constructor(compositionStore: CompositionStore, compositionMaterialUrl: cnl, cache: ICache, compositionKey: string, noSustainPercentage?: number) {
		makeObservable(this);
		this.compositionStore = compositionStore;
		this._isDirty = !isNullOrUndefined(noSustainPercentage);
		this._compositionTypes = [];
		this._key = compositionKey;
		this.compositionMaterialUrl = compositionMaterialUrl;
		this.cache = cache;
		this._noSustainPercentage = noSustainPercentage;
	}
}

export class CompositionStore extends ReactionStore {
	/** cache for this store */
	readonly cache: ICache = new WorkingCache();

	/** the composition map */
	private compositionMap: ObservableMap<string, CompositionMaterial> = observable.map<string, CompositionMaterial>(null, { deep: false });

	/** FormData for the view window */
	readonly formData: CsiFormData;

	/** Locale from useLocale on parent */
	readonly loc: ILocaleProvider;

	/** action for save */
	private readonly _onSave: ControllerSaveFunction;

	/** sustainability flag */
	@observable private _hasSustainability: boolean;

	/** from Theme flag */
	readonly fromTheme: boolean;

	/** Nodes from front end */
	readonly compositionNodes: C8Node[];

	/** percentage attribute */
	readonly percentageAttribute = reflection.getTypeAttribute("MaterialComposition", "Percentage");

	get hasSustainability(): boolean {
		return !this.fromTheme && this._hasSustainability;
	}

	get viewParams(): { bPopup: boolean; defView: View; onCreateMainQueryLevel: any} {
		const { formData, onSelectCompositionMaterial, compositionNodes } = this;
		
		formData.onSelect = onSelectCompositionMaterial;
		
		const rc = reflection.getClass(TYPE_SIZE_CHART_ADMIN);
		const defView = def.getClassViewDef(rc, "CompositionMaterials") as View;
		defView.setFormData(formData);
	
		const resultUrls = compositionNodes?.filter((node) => node.OKforMaterial).map(xml.nodeGetURL);

		return {
			bPopup: true,
			defView: defView,
			onCreateMainQueryLevel: (ql): void => {
				ql.requestParameters = {
					[QRY_LIMIT_PATH]: resultUrls,
				};
			},
		};
	}

	/** if limit is reached return satisfied if not, then return unsatisfied */
	get compositionGuidanceSatisfied(): string {
		if (this.compositionsHaveUnpopulatedPercentages) {
			return "CompositionGuidanceSingleNotSatisfied";
		}
		if (!this.compositionPercentagesAllPositive) {
			return "CompositionGuidanceContainsNegative";
		}
		if (!this.reachLimit) {
			return "CompositionGuidanceNotSatisfied";
		}

		return "CompositionGuidanceSatisfied";
	}

	/** return true if the save button should be disabled */
	get disableSave(): boolean {
		return this.compositionGuidanceSatisfied !== "CompositionGuidanceSatisfied" && this.compositionMap.size > 0;
	}

	/** gets the current composition material library */
	get compositionMaterialUrlLibrary(): Set<cnl> {
		return new Set(this.compositions.map((x) => x.compositionMaterialUrl));
	}

	/** all the compositions */
	@computed get compositions(): CompositionMaterial[] {
		// Get the JSON representation of the ObservableMap
		const json = this.compositionMap.toJSON();
		// Extract the CompositionMaterial objects
		const compositionMaterials = json.map(([_, compositionMaterial]) => compositionMaterial);
		return compositionMaterials;
	}

	/** return true if every composition has a percentage */
	get compositionsHaveUnpopulatedPercentages(): boolean {
		const { compositions, hasSustainability } = this;
		if (hasSustainability) {
			return compositions.some(({ compositionTypes }) => compositionTypes.some(({ percentage }) => !percentage || percentage === 0));
		} else {
			return compositions.some(({ noSustainPercentage }) => !noSustainPercentage || noSustainPercentage === 0);
		}
	}

	/** return true if all percentages are positive */
	get compositionPercentagesAllPositive(): boolean {
		const { compositions, hasSustainability } = this;
		if (hasSustainability) {
			return compositions.every(({ compositionTypes }) => compositionTypes.every(({ percentage }) => percentage >= 0));
		} else {
			return compositions.every(({ noSustainPercentage }) => noSustainPercentage >= 0);
		}
	}

	/** count of compositions */
	get count(): number {
		return this.compositions.length;
	}

	/** the current company custom material in the case of edit */
	get currentMaterialAttribute(): C8Node | null {
		const { formData } = this;
		if (formData?.getNode()?.$Type == "MaterialComposition") {
			return formData.getParent();
		}
		return formData?.getNode() || formData?.getParent();
	}

	/** indicate if in the case of edit composition material */
	get editComposition(): boolean {
		const { currentMaterialAttribute } = this;

		return !!currentMaterialAttribute;
	}

	/** display hint color based on disableSave */
	get hint(): { color: string } {
		return {
			color: this.disableSave ? red[600] : green[600],
		};
	};

	/** indicate if percentage reaches 100% */
	get reachLimit(): boolean {
		const { compositions } = this;
		let amount = 0;

		compositions.forEach((composition) => {
			if (composition.noSustainPercentage > 0) {
				amount += composition.noSustainPercentage;
			} else {
				for (const { percentage } of composition.compositionTypes) {
					if (isNullOrUndefined(percentage)) {
						continue;
					}
					amount += percentage;
				};
			}
		});

		return (getPercentageNumber(amount, this.percentageAttribute.getFormat()) === 1);
	}

	/** does the composition map have a certain composition material */
	private _hasCompositionMaterial = (compositionMaterialUrl: string): boolean => {
		return this.compositionMaterialUrlLibrary.has(compositionMaterialUrl);
	};

	/** load the database with the selections and inputs from the view */
	onSave = async (): Promise<boolean> => {
		const { currentMaterialAttribute, compositions, _onSave } = this;
		const w = new Writer();

		const compositionUrlsAfterAdditions = [];
		const compositionTypes = [];
		const percentages = [];

		compositions.forEach((composition) => {
			const currentUrl = composition.compositionMaterialUrl;
			if (composition.noSustainPercentage > 0) {
				compositionUrlsAfterAdditions.push(currentUrl);
				compositionTypes.push(DUMMY);
				percentages.push(roundToNearestEighth(composition.noSustainPercentage));
			} else {
				composition.compositionTypes.forEach((compositionType) => {
					compositionUrlsAfterAdditions.push(currentUrl);
					compositionTypes.push(compositionType.url);
					percentages.push(roundToNearestEighth(compositionType.percentage));
				});
			}
		});

		w.callMethod("Specification", "CreateMaterialCompositionNodes")
			.addParameter("Compositions", xml.ATTR_REFLIST, compositionUrlsAfterAdditions)
			.addParameter("CompositionTypes", xml.ATTR_REFLIST, compositionTypes)
			.addParameter("Percentages", xml.ATTR_DOUBLEVECTOR, percentages)
			.addParameter("TechnicalCompositionHolder", xml.ATTR_REF, currentMaterialAttribute?.$URL);

		await submitWriter(w);
		_onSave();
		return true;
	};

	/** selecting or deselecting a composition material in the table */
	private onSelectCompositionMaterial = (ctx: Context, url: string, bChecked: boolean, bOld: boolean): void => {
		const { addComposition, removeCompositions } = this;
		if (bChecked) {
			const compositionKey = addComposition(url, ctx.cache);
			const selectedComposition = this.compositionMap.get(compositionKey);
			selectedComposition.addCompositionType(DUMMY);
		} else {
			removeCompositions(url);
		}
	};

	/** add composition */
	@action addComposition = (compositionMaterialUrl: cnl, cache: ICache, noSustainPercentage?: number): string => {
		const { compositionMap, currentMaterialAttribute } = this;
		const key = createCompositionKey(compositionMaterialUrl, currentMaterialAttribute?.Id);
		compositionMap.set(key, new CompositionMaterial(this, compositionMaterialUrl, cache, key, noSustainPercentage));
		return key;
	};

	/** add composition variant */
	addCompositionTypeVariant = (key: string): void => {
		const currentComposition = this.compositionMap.get(key);
		currentComposition.addCompositionType(DUMMY);
	};

	/** Add data from the database to the view */
	loadSelections = (hasSustainability: boolean): void => {
		const { formData, currentMaterialAttribute, cache } = this;

		const compositionNodes = cache.getNodes(currentMaterialAttribute.TechnicalComposition);
		// if the user has sustainibility feature flag load with compositionTypes
		if (hasSustainability) {
			const compositionContainer = [];
			// put compositions and compositionTypes into an object so they are easy to loop through and load
			for (const composition of compositionNodes) {
				const existingComp = compositionContainer.find((comp) => comp.Composition === composition.Composition);
				if (existingComp) {
					existingComp.CompositionType.push({
						Url: composition.SustainabilityCompositionType,
						Percentage: composition.Percentage*100,
					});
				} else {
					compositionContainer.push({
						Composition: composition.Composition,
						CompositionType: [
							{
								Url: composition.SustainabilityCompositionType,
								Percentage: composition.Percentage*100,
							},
						],
					});
				}
			}
			this.loadCompositionMapWithTypeVariants(compositionContainer);
		} else {
			compositionNodes.forEach((compositionNode) => {
				formData.addAggregatedNode(cache.get(compositionNode.Composition));
				this.addComposition(compositionNode.Composition, cache, compositionNode.Percentage*100);
			});
		}
		this.setSustainability(hasSustainability);
	};

	/** set sustainability flag */
	@action setSustainability(hasSustainability: boolean): void {
		this._hasSustainability = hasSustainability;
	};

	/** Addes Compositions to the UI then loads those compositions with type variants  */
	loadCompositionMapWithTypeVariants = (compositionContainer): void => {
		const { formData, cache } = this;
		compositionContainer.forEach((composition) => {
			// add node to the form
			formData.addAggregatedNode(cache.get(composition.Composition));
			// create new composition
			const key = this.addComposition(composition.Composition, cache);
			const compositionFromMap = this.compositionMap.get(key);
			// load that composition with each composition type
			composition.CompositionType.forEach((type) => {
				let urlToLoad = type.Url;
				const possibleTypes = compositionFromMap.possibleSustainabilityCompositionTypes;
				// checks if the possible types exist and if they contain the url to load, if not, then it will grab a new one from the possible types
				if (possibleTypes.length > 0 && !possibleTypes.includes(urlToLoad)) {
					urlToLoad = compositionFromMap.possibleSustainabilityCompositionTypes[0];
				}
				compositionFromMap.addCompositionType(urlToLoad, type.Percentage);
			});
		});
	};

	/** remove composition */
	@action removeComposition = (key: string): void => {
		const { compositionMap, _hasCompositionMaterial, uncheckTableRow } = this;
		const { compositionMaterialUrl } = compositionMap.get(key);
		compositionMap.delete(key);
		if (!_hasCompositionMaterial(compositionMaterialUrl)) {
			uncheckTableRow(compositionMaterialUrl);
		}
	};

	/** remove compositions of a given composition material */
	@action removeCompositions = (compositionMaterialUrl: cnl): void => {
		const { compositionMap } = this;
		const mapKeys = Array.from(keys(compositionMap));
		mapKeys.forEach((key) => {
			if (compositionMap.get(key).compositionMaterialUrl === compositionMaterialUrl) {
				compositionMap.delete(key);
			}
		});
	};

	/** clear out the compositions */
	@action clearComposition = async (): Promise<void> => {
		// clear out the table selections
		const { compositionMaterialUrlLibrary, uncheckTableRow } = this;
		const tableWidget = csiView.getView("CompositionMaterials") as TableWidget;
		const ctx = tableWidget.getContext();
		//Add selected composition nodes into context cache
		await resolveNodes([...compositionMaterialUrlLibrary], ctx.cache);
		compositionMaterialUrlLibrary.forEach((url) => uncheckTableRow(url));

		// clear out the map to make the list in the right pane empty
		this.compositionMap.clear();
	};


	/** uncheck a specific table row */
	uncheckTableRow = (url: cnl): void => {
		const { formData } = this;
		const tableWidget = csiView.getView("CompositionMaterials") as TableWidget;

		// get the target context
		const ctx = tableWidget.getContext(url);
		// unselect the table row
		formData.changeSelection(ctx, false);
	};

	constructor(cache: ICache, formData: CsiFormData, compositionNodes: C8Node[], loc: ILocaleProvider, onSave: ControllerSaveFunction, hasSustainability: boolean, fromTheme: boolean) {
		super();
		makeObservable(this);

		this.formData = formData;
		this.cache = cache;
		this.compositionNodes = compositionNodes;
		this.loc = loc;
		this._hasSustainability = hasSustainability;
		this.fromTheme = fromTheme;
		this._onSave = onSave;
		this.loadSelections(hasSustainability);
	}
}

export const CompositionStoreContext = createContext<CompositionStore>(undefined);
export const useCompositionStore = (): CompositionStore => useContext(CompositionStoreContext);